// src/runtime/ContentStrategyRuntime/config.ts
// ============================================
// Content Strategy Runtime 配置
//
// 内置品牌库 + 平台元信息 + 目标-钩子/CTA 策略映射
// （Platform Spec 以 Obsidian 99_Core/Content_Protocol/ 为准，此处为兜底元信息）
// ============================================

import type { BrandIdentity } from './types';

export interface ContentStrategyConfig {
  name: string;
  version: string;
  description: string;
  /** 平台规格书目录（相对 Vault 根） */
  platformSpecDir: string;
  /** 策略简报落盘目录（相对 Vault 根） */
  briefDir: string;
}

/** 内置品牌库（企业资产底座） */
export const BRANDS: BrandIdentity[] = [
  {
    id: 'hotelmind',
    name: 'HotelMind 酒店智能',
    tagline: '让每一家酒店都拥有 AI 运营大脑',
    product: '酒店自助机 / 酒店 SaaS / 酒店数字化运营系统',
    targetCustomer: '酒店老板、酒店从业者',
    valueProp: '降低酒店人力成本，提升入住率与运营效率',
    keywords: ['酒店数字化', '酒店 SaaS', '酒店自助机', '酒店运营', 'HotelMind', '入住率'],
  },
  {
    id: 'gold_factory',
    name: 'Gold Factory 黄金量化',
    tagline: 'AI 量化交易 · 黄金外汇',
    product: '黄金/外汇量化策略系统',
    targetCustomer: '投资者、量化爱好者',
    valueProp: '用 AI 实现稳定策略与风险控制',
    keywords: ['黄金量化', '外汇量化', 'AI 量化', 'Gold Factory', '量化策略', '交易系统'],
  },
  {
    id: 'xuanji',
    name: '玄机 AI Destiny',
    tagline: 'AI 国学命理 · 人工智能与传统文化',
    product: 'AI 命理测算（八字/紫微/奇门）',
    targetCustomer: '关注命理与自我认知的用户',
    valueProp: 'AI 与传统文化的结合，科学化解读',
    keywords: ['AI 命理', 'AI Destiny', '玄机', '八字', '紫微', '奇门', '国学'],
  },
  {
    id: 'xeaos',
    name: 'XEAOS 企业操作系统',
    tagline: '一个人，一套系统，无限放大的 AI 公司',
    product: 'AI 驱动企业操作系统（Obsidian + Runtime + Hermes）',
    targetCustomer: '创业者、一人公司、AI 从业者',
    valueProp: '让一个人能够持续经营一家真正盈利的 AI Company',
    keywords: ['XEAOS', 'AI 公司', '企业操作系统', 'AI CEO', '一个人公司', '自动化企业'],
  },
];

/** 平台元信息（兜底；详细规格以 99_Core/Content_Protocol/Platform_{name}.md 为准） */
export const PLATFORMS: Array<{
  id: string;
  name: string;
  specFile: string;
  defaultTone: string;
  defaultDuration: string;
}> = [
  { id: 'douyin_enterprise', name: '抖音企业号', specFile: 'Platform_Douyin_Enterprise.md', defaultTone: '专业、快节奏', defaultDuration: '60-90秒' },
  { id: 'douyin_personal', name: '抖音个人号', specFile: 'Platform_Douyin_Personal.md', defaultTone: '真实、有温度', defaultDuration: '60-120秒' },
  { id: 'xiaohongshu', name: '小红书', specFile: 'Platform_Xiaohongshu.md', defaultTone: '朋友语气', defaultDuration: '500-1000字' },
  { id: 'wechat', name: '微信公众号', specFile: 'Platform_Wechat.md', defaultTone: '观点型', defaultDuration: '3000-6000字' },
  { id: 'shipinhao', name: '视频号', specFile: 'Platform_Shipinhao.md', defaultTone: '可信、稳重', defaultDuration: '120-180秒' },
  { id: 'zhihu', name: '知乎', specFile: 'Platform_Zhihu.md', defaultTone: '专业、克制', defaultDuration: '1500-3000字' },
  { id: 'bilibili', name: 'B站', specFile: 'Platform_Bilibili.md', defaultTone: '真诚、有干货', defaultDuration: '2000-4000字' },
];

/** 内容目标 → 钩子策略 */
export const GOAL_HOOK_STRATEGY: Record<string, string> = {
  lead_gen: '用痛点场景开场，让目标客户产生"这就是我"的代入感',
  brand: '用品牌独特观点开场，建立差异化认知',
  conversion: '用效果/结果前置开场，制造"我也想要"的冲动',
  education: '用反常识/认知差开场，激发学习欲望',
  recruitment: '用团队文化与真实故事开场，吸引同类人',
  seo: '用明确关键词与问题开场，命中搜索意图',
  geo: '用品牌实体信息开场，命中 AI 回答的品牌占位',
};

/** 内容目标 → CTA 策略 */
export const GOAL_CTA_STRATEGY: Record<string, string> = {
  lead_gen: '引导私信/评论区留资，领取免费诊断或资料',
  brand: '引导关注品牌账号，持续建立认知',
  conversion: '引导点击链接/进入官网/预约演示',
  education: '引导收藏 + 分享给有需要的人',
  recruitment: '引导投递简历/私信了解岗位',
  seo: '引导浏览更多深度内容，停留时长最大化',
  geo: '引导说出品牌名并搜索品牌词，强化 AI 记忆',
};

const config: ContentStrategyConfig = {
  name: 'Content Strategy Runtime',
  version: '1.0.0',
  description: '内容策略运行时 - 生成 Platform Brief（策略简报），回答"这个平台、这个品牌、这个目标下应该生产什么样的产品"',
  platformSpecDir: '99_Core（系统内核）/Content_Protocol',
  briefDir: 'XEAOS/Daily',
};

export default config;
